import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateServiceSchema, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { SwissLanguage, getCityDisplayName } from '@/lib/swissI18n';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle, Phone, Clock, Star, TrendingUp, Zap, Calculator } from 'lucide-react';

const ZurichServicePage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const language = currentLanguage as SwissLanguage;
  
  // Extract service and city from pathname
  const pathname = window.location.pathname;
  const cityServiceMatch = pathname.match(/\/(ai-video|webdesign|seo|google-business-profile)\/(zuerich|basel|bern)/);
  const service = cityServiceMatch ? cityServiceMatch[1] : 'ai-video';
  const city = cityServiceMatch ? cityServiceMatch[2] : 'zuerich';
  
  // Zürich-specific content for each service
  const zurichContent = {
    'ai-video': {
      title: 'AI-Video Zürich – Mehr Conversions in 14 Tagen | Future Level',
      description: 'AI-Video Produktion in Zürich: KI-gestützte Videos für mehr Conversions. Schnelle Produktion, A/B-Testing, Untertitel DE/FR/IT/EN. Jetzt kostenloses Konzept sichern.',
      h1: 'AI-Video Zürich',
      subtitle: 'Mehr Conversions in 14 Tagen – KI-Videos aus der Limmatstadt',
      hero: 'Zürich ist das Schweizer Zentrum für Innovation – und Ihre Videos sollten es auch sein. Wir produzieren KI-gestützte Videos, die Zürcher Unternehmen messbar mehr Kunden bringen.',
      
      localProof: [
        'Bereits 47+ Zürcher Unternehmen vertrauen uns',
        'Durchschnittlich +180% mehr Conversions in Zürich',
        'Partner von Swiss Startup Association'
      ],
      
      zurichSpecial: 'Zürich Business Package: CHF 3\'900 statt CHF 4\'500',
      
      faq: [
        {
          question: 'Warum AI-Videos speziell für Zürich?',
          answer: 'Zürcher Unternehmen sind tech-affin und erwarten Innovation. AI-Videos sprechen diese Zielgruppe direkt an und konvertieren 3x besser als klassische Videos bei der anspruchsvollen Zürcher Kundschaft.'
        },
        {
          question: 'Können Sie Videos auf Schweizerdeutsch erstellen?',
          answer: 'Ja, wir produzieren Videos in Hochdeutsch und können Züridüütsch-Elemente integrieren für authentische lokale Ansprache. Besonders effektiv für B2C in der Region Zürich.'
        },
        {
          question: 'Wie schnell sind AI-Videos in Zürich verfügbar?',
          answer: 'Erste Videos in 3-5 Tagen, komplette Kampagne in 14 Tagen. Express-Service für Zürcher Startups: 48h-Turnaround möglich.'
        },
        {
          question: 'Arbeiten Sie mit Zürcher Unternehmen zusammen?',
          answer: 'Ja, wir haben bereits mit Unternehmen aus Zürich-West, der Bahnhofstrasse und dem Technopark gearbeitet. Referenzen auf Anfrage.'
        }
      ]
    },
    
    'webdesign': {
      title: 'Webdesign Zürich – In 14 Tagen live, SEO-ready | Future Level',
      description: 'Webdesign Zürich: Moderne Website-Entwicklung für Zürcher Unternehmen. Design + Technik + SEO aus einer Hand. Mehrsprachig, blitzschnell. Gratis Audit anfordern.',
      h1: 'Webdesign Zürich',
      subtitle: 'In 14 Tagen live – Websites, die Zürcher überzeugen',
      hero: 'Zürich verdient erstklassige Websites. Wir entwickeln moderne, mehrsprachige Websites, die bei Zürcher Kunden ankommen und bei Google auf Seite 1 ranken.',
      
      localProof: [
        '89+ Websites für Zürcher Unternehmen',
        'Durchschnittlich 4.8/5 Sterne von Zürich-Kunden',
        'Spezialist für Zürich-Ost bis Zürich-West'
      ],
      
      zurichSpecial: 'Zürich-Paket: Website + lokales SEO für CHF 5\'900',
      
      faq: [
        {
          question: 'Was kostet Webdesign in Zürich konkret?',
          answer: 'Zürcher Business-Websites ab CHF 4\'500. Mehrsprachige E-Commerce ab CHF 7\'900. Premium-Websites für Bahnhofstrasse-Standards ab CHF 12\'500. Fixpreise, keine Überraschungen.'
        },
        {
          question: 'Verstehen Sie den Zürcher Markt?',
          answer: 'Absolut. Wir kennen die hohen Ansprüche der Zürcher Kundschaft an Design und Funktionalität. Unsere Websites spiegeln die Professionalität wider, die Zürich erwartet.'
        },
        {
          question: 'Bieten Sie Wartung für Zürcher Websites?',
          answer: 'Ja, mit lokalem Support. Updates, Security, Performance-Monitoring. Wartungsverträge ab CHF 299/Monat für Zürcher Unternehmen.'
        },
        {
          question: 'Wie funktioniert mehrsprachiges SEO für Zürich?',
          answer: 'DE-CH für lokale Zürcher Suche, EN für internationale Firmen in Zürich, optional FR/IT für schweizweite Expansion. Korrekte hreflang-Implementation inklusive.'
        }
      ]
    },
    
    'seo': {
      title: 'SEO Agentur Zürich – Technisches & Multilingual SEO | Future Level',
      description: 'SEO Agentur Zürich: de-CH, fr-CH, it-CH Optimierung. Technisches SEO, Content, Backlinks. Sichtbar in der ganzen Schweiz. Strategiegespräch buchen.',
      h1: 'SEO Agentur Zürich',
      subtitle: 'Sichtbar in der ganzen Schweiz – SEO aus der Limmatstadt',
      hero: 'Von Zürich aus die ganze Schweiz erobern. Unser technisches und multilinguales SEO macht Zürcher Unternehmen in allen Sprachregionen sichtbar.',
      
      localProof: [
        '156+ Zürcher Unternehmen auf Seite 1',
        'Durchschnittlich +240% organischer Traffic in Zürich',
        'Spezialist für Zürich + schweizweite Expansion'
      ],
      
      zurichSpecial: 'Zürich SEO-Paket: Lokales + nationales SEO für CHF 3\'500/Monat',
      
      faq: [
        {
          question: 'Was kostet SEO für Zürcher Unternehmen?',
          answer: 'SEO-Audit CHF 1\'800, laufende Optimierung ab CHF 3\'200/Monat. Premium SEO für Grossraum Zürich ab CHF 5\'500/Monat. ROI-orientierte Preisgestaltung.'
        },
        {
          question: 'Wie wichtig ist lokales SEO für Zürich?',
          answer: 'Extrem wichtig. "Restaurant Zürich", "Anwalt Zürich", "Webdesign Zürich" - 67% aller Zürcher Suchen haben lokalen Bezug. Wir optimieren für Stadt + Kreis.'
        },
        {
          question: 'Können Sie schweizweites SEO von Zürich aus?',
          answer: 'Ja, das ist unser Spezialgebiet. Von Zürich aus optimieren wir für Basel, Bern, Genf, Lausanne. Multilingual (DE/FR/IT) mit lokalen Anpassungen.'
        },
        {
          question: 'Wie messen Sie SEO-Erfolg in Zürich?',
          answer: 'Lokale Rankings ("SEO Zürich", "Webdesign Zürich"), Google My Business Performance, organischer Traffic aus Zürich + Umgebung. Monatliche Zürich-Reports.'
        }
      ]
    },
    
    'google-business-profile': {
      title: 'Google Unternehmensprofil Zürich – Mehr Anrufe & Bewertungen | Future Level',
      description: 'Google Business Profile Optimierung Zürich: Lokale Sichtbarkeit steigern, Rezensionen aufbauen, Posts & Angebote managen. Jetzt Setup & Optimierung sichern.',
      h1: 'Google Business Profile Zürich',
      subtitle: 'Mehr Anrufe aus Zürich – lokale Sichtbarkeit steigern',
      hero: 'Zürich ist voller Geschäftsmöglichkeiten. Wir sorgen dafür, dass Ihr Google Business Profile bei "Zürich + Ihre Dienstleistung" ganz oben erscheint.',
      
      localProof: [
        '234+ Zürcher Unternehmen optimiert',
        'Durchschnittlich +320% mehr Anrufe aus Zürich',
        'Partner von Standort Zürich'
      ],
      
      zurichSpecial: 'Zürich Local Package: GBP + Citations für CHF 1\'290',
      
      faq: [
        {
          question: 'Warum Google Business Profile speziell für Zürich?',
          answer: 'Zürich hat enorme lokale Konkurrenz. 89% suchen "in Zürich" oder "Zürich-Ost/West". Ein optimiertes GBP entscheidet über Sichtbarkeit vs. Unsichtbarkeit.'
        },
        {
          question: 'Wie bekomme ich mehr Zürcher Bewertungen?',
          answer: 'Systematische Nachfrage bei Zürcher Kunden, Review-Links per QR-Code, automatisierte Follow-ups. Wir helfen dabei, authentische 5-Sterne Bewertungen zu sammeln.'
        },
        {
          question: 'Was sind Zürich-spezifische Optimierungen?',
          answer: 'Kategorien für Zürich, lokale Keywords (Zürich-Ost, Zürich-West, Seefeld, Wiedikon), Zürcher Events-Integration, lokale Citations (Zürich-Verzeichnisse).'
        },
        {
          question: 'Funktioniert das auch für Zürich-Umgebung?',
          answer: 'Ja, wir optimieren für Grossraum Zürich: Winterthur, Uster, Dübendorf, Kloten. Multi-Location Setup für Unternehmen mit mehreren Standorten.'
        }
      ]
    }
  };

  const currentService = service || 'ai-video';
  const content = zurichContent[currentService as keyof typeof zurichContent];
  
  useEffect(() => {
    if (!content) return;

    // Update meta tags with Zürich-specific content
    updateMetaTags({
      title: content.title,
      description: content.description,
      canonicalUrl: `https://www.future-level.ch/de/${currentService}/zuerich`,
      language: 'de',
      keywords: `${currentService}, Zürich, Schweiz, ${currentService} Agentur Zürich`
    });

    // Update hreflang for Zürich city page
    const hreflangLinks = generateHreflangLinks({
      currentLanguage: 'de',
      currentPath: `/${currentService}/zuerich`,
      availableLanguages: ['de', 'fr', 'it', 'en']
    });
    updateHreflangLinks(hreflangLinks);

    // Inject comprehensive schemas
    const orgSchema = generateOrganizationSchema({ language: 'de' });
    const serviceSchema = generateServiceSchema({
      serviceName: content.h1,
      serviceSlug: currentService,
      city: 'zuerich',
      language: 'de',
      priceRange: 'CHF 1500-8500',
      description: content.description,
      url: `https://www.future-level.ch/de/${currentService}/zuerich`
    });
    const breadcrumbSchema = generateBreadcrumbSchema('de', currentService, 'zuerich');
    const faqSchema = generateFAQSchema({ 
      questions: content.faq, 
      language: 'de' 
    });

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema, breadcrumbSchema, faqSchema]
    });
  }, [currentService, content]);

  if (!content) {
    return <div>Service not found</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section with Zürich Focus */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-purple-600/20 text-purple-200 border-purple-500/30">
                <MapPin className="w-4 h-4 mr-2" />
                Zürich Spezialist • Limmatstadt
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {content.h1}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent block">
                  {content.subtitle}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {content.hero}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Phone className="w-5 h-5 mr-2" />
                  Kostenloses Zürich-Audit
                </Button>
                {currentService === 'webdesign' ? (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20"
                    onClick={() => navigate('/de/website-kosten-rechner')}
                  >
                    <Calculator className="w-5 h-5 mr-2" />
                    Website-Kosten berechnen
                  </Button>
                ) : currentService === 'seo' ? (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20"
                    onClick={() => navigate('/de/seo-roi-rechner')}
                  >
                    <TrendingUp className="w-5 h-5 mr-2" />
                    SEO ROI berechnen
                  </Button>
                ) : (
                  <Button size="lg" variant="outline" className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20">
                    <Clock className="w-5 h-5 mr-2" />
                    15-Min Beratung buchen
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Zürich Local Proof Section */}
        <section className="py-16 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-8">
                Warum Zürcher Unternehmen uns vertrauen
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {content.localProof.map((proof, index) => (
                  <Card key={index} className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700">
                    <CardContent className="p-6 text-center">
                      <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-gray-300">{proof}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Zürich Special Offer */}
              <div className="mt-12 p-6 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30">
                <h3 className="text-xl font-bold text-white mb-2">
                  <Zap className="inline w-5 h-5 mr-2 text-yellow-400" />
                  Exklusiv für Zürich
                </h3>
                <p className="text-purple-200 text-lg">{content.zurichSpecial}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Zürich FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white text-center mb-12">
                Häufige Fragen aus Zürich
              </h2>
              
              <div className="space-y-6">
                {content.faq.map((item, index) => (
                  <Card key={index} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3">{item.question}</h3>
                      <p className="text-gray-300">{item.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Zürich CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Bereit für Zürich-Erfolg?
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                Kostenloses Audit speziell für Zürcher Unternehmen. Von der Limmatstadt für die Limmatstadt.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                  <MapPin className="w-5 h-5 mr-2" />
                  Zürich-Audit anfordern
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Star className="w-5 h-5 mr-2" />
                  Zürich-Referenzen ansehen
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ZurichServicePage;